import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Heading, BodyText, Button } from '@aaimplatform/ui';
import { TrendingUp as TrendingUpIcon, ExternalLink as ExternalLinkIcon } from 'lucide-react';
import { useAuthStore } from '../stores/auth';

/**
 * Strategy Tools landing card.
 *
 * Sprint C12 Lane 5: Phase 1 of strategy-tools embedding decision -- hardened
 * external new-tab navigation. Operator clicks open a new tab to the
 * strategy-tools SPA (port 9305 in dev, /strategy-tools/ in prod) with
 * URL params carrying operator context. A postMessage listener handles a
 * return signal from the strategy-tools window. Iframe-with-auth-bridge
 * and Module Federation are deferred to C13+.
 *
 * Operator context conveyed via URL params:
 *  - tenantId: from useAuthStore().user.tenantId (when present)
 *  - returnUrl: encodeURIComponent(window.location.href) so strategy-tools
 *    can post back a return signal that resolves to this page.
 *  - institutionId: NOT included in C12. There is no "selected institution"
 *    context in platform-admin's auth store today. Adding a fabricated
 *    value (e.g., reusing tenantId) would conflate operator-tenant with
 *    institution-being-administered, which is semantically wrong per the
 *    identity-domains model. C13 will wire selected-institution context
 *    when it exists.
 *
 * The endpoint host (strategy-tools SPA) is on a separate origin in dev,
 * so localStorage and cookies are not shared. Auth bridging (the open
 * question for C13+) is the reason this page is a deliberate hand-off
 * rather than an embed.
 */

// Frontend SPA cross-handoff URL (NOT the backend API URL -- that is
// VITE_STRATEGY_TOOLS_API_URL, wired separately through api-clients.ts).
// In production behind LBs, set VITE_STRATEGY_TOOLS_URL to the canonical
// strategy-tools origin (e.g. https://strategy.aaim.com per Sprint 3 corp-portal).
// In dev, set VITE_STRATEGY_TOOLS_URL=http://localhost:9305 in .env.local.
// Empty default resolves to the relative '/strategy-tools/' path which 404s
// visibly when no LB rule exists, surfacing missing config rather than
// silently calling a user's localhost.
// See knowledge-graph/projects/aaim/technology-innovation/incidents/2026-05-13-ops-console-auth-url-localhost-bake.md
const STRATEGY_TOOLS_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_STRATEGY_TOOLS_URL)
  || '/strategy-tools/';

/**
 * Resolve the absolute origin of STRATEGY_TOOLS_URL for postMessage origin
 * verification. For relative paths (production), origin is window.origin.
 */
function resolveStrategyToolsOrigin(): string {
  if (typeof window === 'undefined') return '';
  try {
    return new URL(STRATEGY_TOOLS_URL, window.location.origin).origin;
  } catch {
    return '';
  }
}

/**
 * Build the launch URL with operator-context query params. Keys with no
 * value are omitted from URLSearchParams entirely (no `tenantId=undefined`
 * literals). returnUrl is always included.
 */
function buildLaunchUrl(params: { tenantId?: string; returnUrl: string }): string {
  const search = new URLSearchParams();
  if (params.tenantId) {
    search.set('tenantId', params.tenantId);
  }
  // TODO(C13): wire selected-institution context. Until platform-admin
  // tracks which institution the operator is currently administering,
  // institutionId is omitted rather than fabricated.
  search.set('returnUrl', params.returnUrl);
  const separator = STRATEGY_TOOLS_URL.includes('?') ? '&' : '?';
  return `${STRATEGY_TOOLS_URL}${separator}${search.toString()}`;
}

interface StrategyToolsReturnMessage {
  type: 'aaim-return';
  payload?: { fallbackUrl?: string };
}

function isReturnMessage(value: unknown): value is StrategyToolsReturnMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'aaim-return'
  );
}

export function StrategyToolsPage() {
  const navigate = useNavigate();
  const tenantId = useAuthStore((state) => state.user?.tenantId);

  const launchUrl = useMemo(() => {
    if (typeof window === 'undefined') return STRATEGY_TOOLS_URL;
    return buildLaunchUrl({
      tenantId,
      returnUrl: window.location.href,
    });
  }, [tenantId]);

  useEffect(() => {
    const expectedOrigin = resolveStrategyToolsOrigin();
    if (!expectedOrigin) return;

    const handler = (event: MessageEvent) => {
      // Origin check: reject any postMessage not from the strategy-tools
      // origin. This prevents cross-origin spoofing of the return signal.
      if (event.origin !== expectedOrigin) return;
      if (!isReturnMessage(event.data)) return;

      // History-aware return: prefer navigate(-1) so the operator lands
      // on whichever platform-admin route they came from. Fall back to
      // the explicit fallbackUrl payload if history has no entries.
      if (window.history.length > 1) {
        navigate(-1);
      } else if (event.data.payload?.fallbackUrl) {
        window.location.href = event.data.payload.fallbackUrl;
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [navigate]);

  return (
    <div>
      <div className="mb-6">
        <BodyText variant="ui" className="text-tertiary">
          Strategy Tools -- economic modeling and business intelligence
        </BodyText>
      </div>

      <Card className="p-8">
        <div className="flex flex-col items-center text-center max-w-xl mx-auto">
          <div className="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center mb-4">
            <TrendingUpIcon className="w-6 h-6 text-accent" />
          </div>
          <Heading level={2} size="card" className="font-sans mb-2">
            Strategy Tools Service
          </Heading>
          <BodyText variant="ui" className="text-tertiary mb-4">
            Economic modeling capabilities are hosted in a dedicated Strategy
            Tools service. The service exposes yield-curve analytics, portfolio
            sensitivity scenarios, and asset-class allocation tools used by
            Aaim platform operators.
          </BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm mb-6">
            Strategy Tools opens in a new tab with operator context preserved
            in URL parameters. The application can signal a return when the
            operator finishes their work, and this page will navigate back to
            the previous platform-admin route automatically.
          </BodyText>

          <Button
            variant="primary"
            size="medium"
            onClick={() => window.open(launchUrl, '_blank', 'noopener,noreferrer')}
          >
            <ExternalLinkIcon className="mr-2" size={18} />
            Open Strategy Tools
          </Button>

          <BodyText variant="ui" className="text-xs text-tertiary mt-2">
            (opens in new tab; signal to return when done)
          </BodyText>

          <BodyText variant="ui" className="text-xs text-tertiary mt-6">
            Hosted as a separate Aaim platform service.
          </BodyText>
        </div>
      </Card>
    </div>
  );
}
